import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "../styles/login-otp.css";
import { saveUserAccessCookie, saveUserPermissionsCookie } from "../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const apiKey = process.env.REACT_APP_API_KEY;

function maskApiKey(key) {
    if (!key) return "(missing)";
    if (key.length <= 8) return "***";
    return `${key.slice(0, 4)}...${key.slice(-4)}`;
}

function getLoginErrorMessage(error, requestUrl) {
    const status = error.response?.status;
    const serverMsg = error.response?.data?.message;

    if (serverMsg) return serverMsg;

    if (status === 403) {
        return "API key galat hai — frontend REACT_APP_API_KEY aur server .env API_KEY same honi chahiye.";
    }
    if (status === 401) {
        return "Username ya password galat hai.";
    }
    if (status === 500) {
        return "Server error (500) — SMTP ya database check karo. Console mein details dekho.";
    }
    if (error.code === "ERR_NETWORK" || !error.response) {
        if (!BASE_URL) {
            return "REACT_APP_BASE_URL set nahi — npm run build dubara karo.";
        }
        return (
            `API server respond nahi kar raha (${requestUrl}). ` +
            `Browser mein kholo: ${BASE_URL}/health — agar 404 HTML aaye to cPanel mein Node.js app start/restart karein (server subdomain). ` +
            `Yeh CORS ya password issue nahi; Node API band hai.`
        );
    }
    return `Request failed — HTTP ${status || "?"} (${error.message || "unknown"})`;
}

function logLoginNetworkError(action, error, requestUrl) {
    const summary = getLoginErrorMessage(error, requestUrl);
    const row = {
        action,
        url: requestUrl,
        baseUrl: BASE_URL || "(missing)",
        apiKey: maskApiKey(apiKey),
        httpStatus: error.response?.status ?? "NO RESPONSE",
        httpStatusText: error.response?.statusText ?? "-",
        axiosCode: error.code ?? "-",
        axiosMessage: error.message,
    };

    console.group(`%c[CRM Login] ${action} FAILED`, "color:#dc3545;font-weight:bold");
    console.error("User message:", summary);
    console.table(row);
    console.log("Server response body:", error.response?.data ?? null);
    console.log("Response headers:", error.response?.headers ?? null);
    console.log("Full error object:", error);

    if (!error.response) {
        console.warn(
            "NO HTTP RESPONSE — common causes:\n" +
                "• Server band hai ya galat URL\n" +
                "• HTTPS site se HTTP API call (mixed content)\n" +
                "• CORS block\n" +
                "• SSL certificate issue on API subdomain\n" +
                `• Check in browser: open ${BASE_URL}/getcountries`
        );
    } else if (error.response.status === 403) {
        console.warn("403 = API_KEY mismatch between build (.env.production) and server (.env)");
    } else if (error.response.status === 500) {
        console.warn("500 = server ne error di — often SMTP (login-otp-smtp.json) ya DB");
    }

    console.groupEnd();
    return summary;
}

export default function Login() {
    const navigate = useNavigate();

    const [step, setStep] = useState("credentials");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [targetEmail, setTargetEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [lastNetworkError, setLastNetworkError] = useState("");
    const [serverStatus, setServerStatus] = useState("checking");

    useEffect(() => {
        const sendOtpUrl = `${BASE_URL || ""}/login/send-otp`;
        console.info("[CRM Login] Loaded — check API config:", {
            BASE_URL: BASE_URL || "MISSING (rebuild with .env.production)",
            REACT_APP_API_KEY: apiKey ? maskApiKey(apiKey) : "MISSING",
            sendOtpUrl,
            tip: "Login try karte waqt F12 → Console / Network tab kholo",
        });

        if (!BASE_URL) {
            setServerStatus("missing_url");
            return;
        }

        axios
            .get(`${BASE_URL}/health`, { timeout: 12000 })
            .then((res) => {
                if (res.data?.status === "ok") setServerStatus("ok");
                else setServerStatus("down");
            })
            .catch(() => setServerStatus("down"));
    }, []);

    const finishLogin = (role, countryAccess, userPermissions) => {
        const date = new Date();
        date.setTime(date.getTime() + 48 * 60 * 60 * 1000);
        Cookies.set("username", username.trim(), { expires: date });
        Cookies.set("user_role", role, { expires: date });
        saveUserAccessCookie(countryAccess, date);
        if (userPermissions) {
            saveUserPermissionsCookie(userPermissions, date);
        }
        toast.success("Login successful!");
        navigate(0);
    };

    const sendOtp = async (e) => {
        if (e) e.preventDefault();
        if (!username.trim() || !password) {
            toast.error("Username and password are required");
            return;
        }
        setSubmitting(true);
        setLastNetworkError("");
        const requestUrl = `${BASE_URL}/login/send-otp`;
        try {
            const res = await axios.post(
                requestUrl,
                { Username: username.trim(), Password: password },
                {
                    headers: {
                        Authorization: apiKey,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (res.data.skipOtp) {
                finishLogin(res.data.role, res.data.countryAccess, res.data.userPermissions);
                return;
            }

            setTargetEmail(res.data.maskedEmail || res.data.email || "your registered email");
            setStep("otp");
            setOtp("");
            toast.success(`OTP sent to ${res.data.maskedEmail || "your email"}`);
        } catch (error) {
            const msg = logLoginNetworkError("Send OTP", error, requestUrl);
            setLastNetworkError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const verifyOtp = async (e) => {
        e.preventDefault();
        if (!otp || otp.length !== 6) {
            toast.error("Enter the complete 6-digit OTP code");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(
                `${BASE_URL}/login/verify-otp`,
                { Username: username.trim(), Otp: otp },
                {
                    headers: {
                        Authorization: apiKey,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (res.data.message) {
                finishLogin(res.data.role, res.data.countryAccess, res.data.userPermissions);
            }
        } catch (error) {
            const requestUrl = `${BASE_URL}/login/verify-otp`;
            const msg = logLoginNetworkError("Verify OTP", error, requestUrl);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const backToCredentials = () => {
        setStep("credentials");
        setOtp("");
        setTargetEmail("");
    };

    return (
        <div className="login-page">
            <ToastContainer position="top-center" />
            <div className="login-card">
                {serverStatus === "down" && (
                    <div className="login-server-alert" role="alert">
                        <strong>API server offline</strong>
                        <p className="mb-1">
                            <a href={`${BASE_URL}/health`} target="_blank" rel="noreferrer">
                                {BASE_URL}/health
                            </a>{" "}
                            abhi JSON nahi de raha (LiteSpeed 404 ya &quot;Index of /&quot;).
                        </p>
                        <p className="mb-0 small">
                            cPanel → <strong>Setup Node.js App</strong> → server folder → Startup{" "}
                            <code>index.js</code> → <strong>Run NPM Install</strong> → Restart. Guide:{" "}
                            <code>server/CPANEL-NODE-SETUP.txt</code>
                        </p>
                    </div>
                )}
                {serverStatus === "missing_url" && (
                    <div className="login-server-alert" role="alert">
                        <strong>Build config missing</strong>
                        <p>REACT_APP_BASE_URL set nahi. .env.production check karke npm run build dubara karein.</p>
                    </div>
                )}

                {step === "otp" && (
                    <div className="login-steps">
                        <div className="login-step done">1. Account</div>
                        <div className="login-step active">2. OTP Code</div>
                    </div>
                )}

                {step === "credentials" && (
                    <>
                        <h2>CRM Portal Login</h2>
                        <p className="mb-4">
                            Enter your username and password to sign in.
                            <br />
                            <small className="text-muted">
                                <strong>Administrator</strong> — seedha login (OTP nahi).
                                <br />
                                <strong>User</strong> — registered email par OTP code aayega.
                            </small>
                        </p>
                        <form onSubmit={sendOtp}>
                            <div className="mb-3">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    autoComplete="username"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="form-label">Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                            <button
                                type="submit"
                                className="login-btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? "Signing in..." : "Login"}
                            </button>
                            {lastNetworkError && (
                                <p className="text-danger small mt-3 mb-0" role="alert">
                                    {lastNetworkError}
                                    <br />
                                    <span className="text-muted">
                                        F12 → Console mein poori network details dekho.
                                    </span>
                                </p>
                            )}
                        </form>
                    </>
                )}

                {step === "otp" && (
                    <>
                        <h3>Enter OTP Code</h3>
                        <p>
                            We sent a <strong>6-digit verification code</strong> to the email linked to
                            your account:
                        </p>
                        <div className="text-center">
                            <span className="login-email-badge">
                                <i className="bi bi-envelope me-1"></i>
                                {targetEmail}
                            </span>
                        </div>
                        <p className="form-text mb-4">
                            Apni registered email ka inbox (aur spam) check karein.
                        </p>
                        <form onSubmit={verifyOtp}>
                            <div className="mb-4">
                                <label className="form-label text-center d-block">OTP Code</label>
                                <input
                                    type="text"
                                    className="form-control login-otp-input"
                                    placeholder="000000"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    maxLength={6}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    autoFocus
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                className="login-btn-primary"
                                disabled={submitting || otp.length !== 6}
                            >
                                {submitting ? "Verifying..." : "Verify OTP & Login"}
                            </button>
                            <div className="login-actions-row">
                                <button type="button" className="login-btn-link" onClick={backToCredentials}>
                                    ← Change account
                                </button>
                                <button
                                    type="button"
                                    className="login-btn-link"
                                    onClick={sendOtp}
                                    disabled={submitting}
                                >
                                    Resend OTP
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
